import { useEffect } from 'react';
import { useInstallStore } from '../stores/installStore';

/**
 * Hook to manage PWA install prompt state.
 * - Captures `beforeinstallprompt` event on Chrome/Edge/Android
 * - Detects if app is already installed (standalone mode)
 * - Detects iOS for educational UI fallback
 */
export const useInstallPrompt = () => {
  const {
    canPrompt,
    isInstalled,
    hasDismissed,
    isIOS,
    setDeferredPrompt,
    setIsInstalled,
    setIsIOS,
    promptInstall,
    dismiss,
  } = useInstallStore();

  useEffect(() => {
    // Detect if already installed (standalone mode)
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari standalone check
      (window.navigator as { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS (no beforeinstallprompt support)
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
      !(window as { MSStream?: unknown }).MSStream;
    
    setIsIOS(isIOSDevice);


    // On iOS, we can show educational UI but can't prompt
    if (isIOSDevice) {
      return;
    }

    // Capture beforeinstallprompt event (Chrome/Edge/Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [setDeferredPrompt, setIsInstalled, setIsIOS]);

  // Determine if we should show the install prompt
  // On mobile (iOS or Android), always show if not installed
  // Don't depend on beforeinstallprompt event (Samsung Browser, etc. don't support it)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const shouldShowPrompt = !isInstalled && isMobile;

  return {
    canPrompt,
    isInstalled,
    hasDismissed,
    isIOS,
    shouldShowPrompt,
    promptInstall,
    dismiss,
  };
};

// Type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

